/**
 * Chat realtime client.
 *
 * Thin wrapper around the browser WebSocket API that:
 *   - opens a WebSocket to /api/v1/chat/realtime
 *   - decodes the server→client envelope (chat.message.created,
 *     connection.ready)
 *   - dispatches messages to a typed listener
 *   - implements bounded exponential reconnect (1s, 2s, 4s, 8s, 30s)
 *   - silently ignores unknown event types (malformed frames are
 *     logged at debug level only)
 *
 * The client never sends business commands over the socket. The
 * HTTP POST /chat/channels/{id}/messages endpoint remains the only
 * write authority.
 *
 * Security: the client relies on the existing session cookie and
 * uses the same protocol as the current page so the browser can
 * reuse the session cookie without a Secure/CORS workaround.
 */

import { DecodeEnvelope, ErrInvalidEnvelope, ErrUnknownEnvelope } from './envelope';

export type RealtimeStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface ChatMessageCreatedWire {
  readonly id: string;
  readonly channelId: string;
  readonly senderAccountId: string;
  readonly content: string;
  readonly createdAt: string;
}

export interface ChatRealtimeMessage {
  readonly id: string;
  readonly channelId: string;
  readonly senderAccountId: string;
  readonly content: string;
  readonly createdAt: string;
}

export interface ChatRealtimeListeners {
  onMessage(event: ChatRealtimeMessage): void;
  onConnectionReady(): void;
  onStatusChange(status: RealtimeStatus): void;
  onError(error: Error): void;
}

export interface ChatRealtimeOptions {
  /**
   * Builds the WebSocket URL. Defaults to the production
   * `ws(s)://current-host/api/v1/chat/realtime`.
   */
  readonly urlBuilder?: () => string;
}

const MAX_RECONNECT_DELAY_MS = 30_000;
const BASE_RECONNECT_DELAY_MS = 1_000;
const MAX_ATTEMPTS_BEFORE_GIVING_UP = 10;

// Authentication / authorization rejection handling.
//
// The browser WebSocket API does not expose the original HTTP
// status code after a failed Upgrade; the close event only
// carries a WebSocket close code and reason. The server-side
// origin policy returns 403 and the session validator returns
// 401, but the client cannot tell those apart from a generic
// connection failure without server cooperation.
//
// Rather than block on distinguishing auth from transient
// errors, we rely on the bounded attempt policy (max 10
// reconnect attempts). After 10 attempts we transition to
// "disconnected" and stop reconnecting — the eventual logout
// / session UX lives in the application shell, not in the
// realtime client.
//
// The session-expiry backstop lives on the server: the
// realtime handler revalidates the session every 5 minutes and
// terminates the connection when the token expires.

export class ChatRealtimeClient {
  private socket: WebSocket | null = null;
  private status: RealtimeStatus = 'disconnected';
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private listeners: ChatRealtimeListeners;
  private options: ChatRealtimeOptions;

  constructor(listeners: ChatRealtimeListeners, options: ChatRealtimeOptions = {}) {
    this.listeners = listeners;
    this.options = options;
  }

  connect(): void {
    if (this.closed) return;
    if (this.socket !== null) return;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let url: string;
    try {
      url = this.options.urlBuilder ? this.options.urlBuilder() : defaultUrl();
    } catch (err) {
      this.listeners.onError(err instanceof Error ? err : new Error(String(err)));
      this.scheduleReconnect();
      return;
    }

    try {
      this.socket = new WebSocket(url);
    } catch (err) {
      this.listeners.onError(err instanceof Error ? err : new Error(String(err)));
      this.scheduleReconnect();
      return;
    }

    this.socket.addEventListener('open', () => {
      this.attempt = 0;
      this.setStatus('connected');
    });

    this.socket.addEventListener('message', (ev) => {
      this.handleMessage(ev.data);
    });

    this.socket.addEventListener('close', () => {
      this.cleanup();
      if (this.closed) return;
      this.scheduleReconnect();
    });

    this.socket.addEventListener('error', () => {
      this.listeners.onError(new Error('WebSocket error'));
    });
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close();
      } catch {
        // ignore
      }
      this.socket = null;
    }
    this.setStatus('disconnected');
  }

  isClosed(): boolean {
    return this.closed;
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    this.attempt += 1;
    if (this.attempt > MAX_ATTEMPTS_BEFORE_GIVING_UP) {
      this.setStatus('disconnected');
      this.listeners.onError(new Error('realtime reconnect gave up'));
      return;
    }
    const delay = Math.min(
      BASE_RECONNECT_DELAY_MS * 2 ** (this.attempt - 1),
      MAX_RECONNECT_DELAY_MS,
    );
    // Jitter: ±20%.
    const jitter = delay * 0.2 * (Math.random() * 2 - 1);
    const nextDelay = Math.max(BASE_RECONNECT_DELAY_MS, Math.floor(delay + jitter));
    this.setStatus('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, nextDelay);
  }

  private setStatus(next: RealtimeStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.listeners.onStatusChange(next);
  }

  private cleanup(): void {
    this.socket = null;
  }

  private handleMessage(data: unknown): void {
    if (typeof data !== 'string') {
      return;
    }
    let event;
    try {
      event = DecodeEnvelope(data);
    } catch (err) {
      if (err === ErrUnknownEnvelope) {
        return;
      }
      if (err === ErrInvalidEnvelope) {
        this.listeners.onError(new Error('invalid realtime envelope'));
        return;
      }
      this.listeners.onError(err instanceof Error ? err : new Error(String(err)));
      return;
    }

    if (event.type === 'connection.ready') {
      this.listeners.onConnectionReady();
      return;
    }
    if (event.type === 'chat.message.created') {
      if (event.message && event.message.id) {
        this.listeners.onMessage(event.message);
      }
      return;
    }
  }
}

function defaultUrl(): string {
  if (typeof window === 'undefined') {
    return 'ws://localhost/api/v1/chat/realtime';
  }
  const { protocol, host } = window.location;
  const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${host}/api/v1/chat/realtime`;
}
